import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  RiUser3Line,
  RiShieldCheckLine,
  RiCameraLine,
  RiCheckLine,
} from "react-icons/ri";

import { useUpdateUserMutation } from "actions/users/mutations";
import { useGetUsersByIdQuery } from "actions/users/queries";
import InputDetail from "components/Inputs/InputDetail";
import { usePermissions } from "hooks/usePermissions";
import { getUser } from "redux/features/userSliceWithTokenValidation";
import HeaderFirst from "utils/HeaderFirst";
import SecurityMFACard from "components/MFA/SecurityMFACard";

const Paramètres = () => {
  const [activeTab, setActiveTab] = useState("profil");
  const { control, handleSubmit, reset, setValue } = useForm({
    defaultValues: {
      nom_entreprise: "",
      adresse: "",
      telephone: "",
      email: "",
      photoURL: null,
    },
  });

  const [logoUrl, setLogoUrl] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const user = useSelector(getUser);
  const { data: userStatus, refetch } = useGetUsersByIdQuery(user?.id);
  const navigate = useNavigate();
  const [updateDossier] = useUpdateUserMutation();
  const permissions = usePermissions();

  useEffect(() => {
    if (userStatus) {
      reset({
        nom_entreprise: `${userStatus?.first_name || ""} ${userStatus?.last_name || ""}`.trim(),
        adresse: userStatus?.adresse || "",
        telephone: userStatus?.phone || "",
        email: userStatus?.email || "",
        photoURL: userStatus?.photo_url || "",
      });
      setLogoUrl(userStatus?.photo_url || "");
    }
    refetch();
  }, [userStatus, reset]);

  const nom = (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <h4 className="titre-dossier mb-0">Paramètres du compte</h4>
    </div>
  );

  const description = (
    <div>Gérez vos informations de profil et la sécurité de vos accès</div>
  );

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoUrl(URL.createObjectURL(file));
      setValue("photoURL", file);
    }
  };

  const onSubmit = async (formData) => {
    setIsSubmitting(true);
    const formDataObj = new FormData();
    const parts = (formData.nom_entreprise || "").trim().split(" ");
    formDataObj.append("first_name", parts[0] || "");
    formDataObj.append("last_name", parts.slice(1).join(" ") || "");
    formDataObj.append("adresse", formData.adresse || "");
    formDataObj.append("phone", formData.telephone || "");
    formDataObj.append("email", formData.email || "");
    if (userStatus?.isExpertComptable !== undefined) {
      formDataObj.append("isExpertComptable", userStatus.isExpertComptable);
    }

    if (formData.photoURL instanceof File) {
      formDataObj.append("photoURL", formData.photoURL);
    }

    try {
      const response = await updateDossier({
        id: userStatus.id,
        updateData: formDataObj,
      }).unwrap();

      toast.success("Votre profil a été mis à jour avec succès");
      reset({
        nom_entreprise: `${response.first_name} ${response.last_name}`.trim(),
        adresse: response.adresse || "",
        telephone: response.phone || "",
        email: response.email || "",
        description: response.description || "",
        photoURL: response.photo_url || "",
      });
      setLogoUrl(response.photo_url || "");
      refetch();
    } catch (error) {
      toast.error(error?.data?.detail || error.message || "Erreur lors de la mise à jour");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    toast.info("Modifications annulées");
    navigate(-1);
  };

  return (
    <div
      style={{
        backgroundColor: "#ffffff",
        minHeight: "600px",
        borderRadius: "20px",
        padding: "24px",
        boxShadow: "0 2px 12px rgba(0,0,0,0.03)",
      }}
    >
      {/* ── EN-TÊTE PRINCIPAL ── */}
      <div className="header pb-3 mb-3 border-bottom">
        <HeaderFirst
          nom={nom}
          description={description}
          enterprise={"Cabinet"}
        />
      </div>

      {/* ── ONGLETS FLUIDES (TABS) ── */}
      <div className="d-flex align-items-center gap-2 mb-4 p-1 bg-light rounded-pill" style={{ maxWidth: "420px" }}>
        <button
          type="button"
          onClick={() => setActiveTab("profil")}
          className="btn rounded-pill flex-fill d-inline-flex align-items-center justify-content-center gap-2 py-2"
          style={{
            fontSize: "14px",
            fontWeight: activeTab === "profil" ? 600 : 500,
            backgroundColor: activeTab === "profil" ? "#ffffff" : "transparent",
            color: activeTab === "profil" ? "#009688" : "#64748b",
            boxShadow: activeTab === "profil" ? "0 2px 8px rgba(0,0,0,0.06)" : "none",
            border: "none",
            transition: "all 0.2s ease-in-out",
          }}
        >
          <RiUser3Line size={17} />
          <span>Mon profil</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("securite")}
          className="btn rounded-pill flex-fill d-inline-flex align-items-center justify-content-center gap-2 py-2"
          style={{
            fontSize: "14px",
            fontWeight: activeTab === "securite" ? 600 : 500,
            backgroundColor: activeTab === "securite" ? "#ffffff" : "transparent",
            color: activeTab === "securite" ? "#009688" : "#64748b",
            boxShadow: activeTab === "securite" ? "0 2px 8px rgba(0,0,0,0.06)" : "none",
            border: "none",
            transition: "all 0.2s ease-in-out",
          }}
        >
          <RiShieldCheckLine size={17} />
          <span>Sécurité & 2FA</span>
        </button>
      </div>

      {/* ── CONTENU DE L'ONGLET 1 : MON PROFIL ── */}
      {activeTab === "profil" && (
        <div className="row g-4">
          <div className="col-lg-8">
            <form onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column gap-3">
              <div
                className="p-4 rounded-4"
                style={{
                  backgroundColor: "#ffffff",
                  border: "1px solid #eef2f6",
                  borderRadius: "16px",
                }}
              >
                <h6 className="mb-3" style={{ fontWeight: 700, color: "#0f172a" }}>
                  Informations personnelles
                </h6>

                {/* Photo de profil */}
                <div className="d-flex align-items-center gap-4 mb-4 pb-3 border-bottom">
                  <div
                    style={{
                      width: "84px",
                      height: "84px",
                      borderRadius: "50%",
                      overflow: "hidden",
                      border: "2px solid #e2e8f0",
                      backgroundColor: "#f8fafc",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                      flexShrink: 0,
                    }}
                  >
                    {logoUrl ? (
                      <img
                        src={logoUrl}
                        alt="Profil"
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <RiUser3Line size={36} style={{ color: "#94a3b8" }} />
                    )}
                  </div>
                  <div>
                    <label
                      htmlFor="photoInput"
                      className="btn btn-outline-secondary btn-sm rounded-pill d-inline-flex align-items-center gap-2 px-3 py-2 mb-1"
                      style={{ cursor: "pointer", fontSize: "13px", fontWeight: 500 }}
                    >
                      <RiCameraLine size={16} />
                      <span>{logoUrl ? "Changer de photo" : "Ajouter une photo"}</span>
                      <input
                        type="file"
                        id="photoInput"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={handleLogoChange}
                      />
                    </label>
                    <p className="text-muted mb-0" style={{ fontSize: "12px" }}>
                      Formats acceptés : JPG, PNG. Taille recommandée : 400x400 px.
                    </p>
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-md-6">
                    <Controller
                      name="nom_entreprise"
                      control={control}
                      render={({ field }) => (
                        <InputDetail
                          label="Nom et Prénom"
                          placeholder="Ex: Amadou Diallo"
                          color="#0f172a"
                          fontWeight={500}
                          fontSize={14}
                          {...field}
                        />
                      )}
                    />
                  </div>
                  <div className="col-md-6">
                    <Controller
                      name="adresse"
                      control={control}
                      render={({ field }) => (
                        <InputDetail
                          label="Adresse"
                          placeholder="Ex: Dakar, Sénégal"
                          color="#0f172a"
                          fontWeight={500}
                          fontSize={14}
                          {...field}
                        />
                      )}
                    />
                  </div>
                  <div className="col-md-6">
                    <Controller
                      name="telephone"
                      control={control}
                      render={({ field }) => (
                        <InputDetail
                          label="Numéro de téléphone"
                          placeholder="Ex: +221 77 000 00 00"
                          color="#0f172a"
                          fontWeight={500}
                          fontSize={14}
                          {...field}
                        />
                      )}
                    />
                  </div>
                  <div className="col-md-6">
                    <Controller
                      name="email"
                      control={control}
                      render={({ field }) => (
                        <InputDetail
                          label="Adresse e-mail"
                          placeholder="Ex: contact@entreprise.sn"
                          color="#0f172a"
                          fontWeight={500}
                          fontSize={14}
                          {...field}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>

              {/* Boutons d'action */}
              {permissions.MANAGE_FOLDER_SETTINGS && (
                <div className="d-flex justify-content-end gap-2 pt-2">
                  <button
                    type="button"
                    className="btn btn-light rounded-pill px-4"
                    style={{ fontSize: "14px", fontWeight: 500 }}
                    onClick={handleCancel}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="btn rounded-pill px-4 d-inline-flex align-items-center gap-2"
                    style={{
                      backgroundColor: "#009688",
                      borderColor: "#009688",
                      color: "#ffffff",
                      fontSize: "14px",
                      fontWeight: 600,
                    }}
                    disabled={isSubmitting}
                  >
                    <RiCheckLine size={16} />
                    <span>{isSubmitting ? "Enregistrement..." : "Enregistrer les modifications"}</span>
                  </button>
                </div>
              )}
            </form>
          </div>

          {/* Carte latérale d'aide */}
          <div className="col-lg-4">
            <div
              className="p-4 rounded-4"
              style={{
                backgroundColor: "#f8fafc",
                border: "1px solid #eef2f6",
                borderRadius: "16px",
              }}
            >
              <h6 className="mb-2" style={{ fontWeight: 700, color: "#0f172a" }}>
                Conseil sécurité
              </h6>
              <p className="text-muted mb-3" style={{ fontSize: "13px", lineHeight: 1.5 }}>
                Veillez à maintenir votre adresse e-mail et votre numéro de téléphone à jour. Ils sont indispensables pour la récupération de votre compte en cas d&apos;oubli de mot de passe.
              </p>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm rounded-pill d-inline-flex align-items-center gap-2"
                onClick={() => setActiveTab("securite")}
                style={{ fontSize: "13px" }}
              >
                <RiShieldCheckLine size={16} />
                <span>Gérer ma double authentification</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── CONTENU DE L'ONGLET 2 : SÉCURITÉ & 2FA ── */}
      {activeTab === "securite" && (
        <div className="row">
          <div className="col-lg-9">
            <SecurityMFACard />
          </div>
        </div>
      )}
    </div>
  );
};

export default Paramètres;
